import dayjs from 'dayjs';
import React from 'react';

import { adminGetDashboardDataApi } from '../api/dashboard/adminGetDashboardData';
import { adminListApplicationUsersMarketsAsOptionsApi } from '../api/markets/adminListApplicationUsersMarketsAsOptionsApi';
import { adminListMarketsAsOptionsApi } from '../api/markets/adminListMarketsAsOptionsApi';
import { adminListVenuesMarketsAsOptionsApi } from '../api/markets/adminListVenuesMarketsAsOptionsApi';
import { adminGetNextPendingUserApi } from '../api/users/adminGetNextPendingUserApi';
import { AdminListMarketsFilter, AdvancedDashboardData } from '../graphql/API';
import { useAdminRole } from '../hooks/useAdminRole';
import { useAsync } from '../hooks/useAsync';
import { subtractDays } from '../utils/date-utils';
import { OptionEntry } from '../utils/generate-options';

export type AppDataContext = {
  dashboardData?: AdvancedDashboardData;
  logResources?: OptionEntry[];
  markets?: OptionEntry[];
  venuesMarkets?: OptionEntry[];
  venuesLeadsMarkets?: OptionEntry[];
  applicationUsersMarkets?: OptionEntry[];
  reloadDashboardData(): void;
  selectedPeriod: SelectedPeriod;
  setSelectedPeriod(period: { fromDate: Date; toDate: Date; by: 'day' | 'week' }): void;
  consumePendingUser(userId: string): string;
  handleLoadApplicationUsersMarkets?: (filter: AdminListMarketsFilter) => void;
  userApplicationsListIds: string[];
  setUserApplicationsListIds(ids: string[]): void;
  venueApplicationsListIds: string[];
  setVenueApplicationsListIds(ids: string[]): void;
  offerApplicationsListIds: string[];
  setOfferApplicationsListIds(ids: string[]): void;
};

export type SelectedPeriodBy = 'day' | 'week';

export type SelectedPeriod = {
  periodSelectorType: string;
  fromDate: Date;
  toDate: Date;
  by: SelectedPeriodBy;
};

const getDefaultPeriod = () => {
  return {
    periodSelectorType: 'Week',
    fromDate: subtractDays(new Date(), 7),
    toDate: new Date(),
    by: 'day',
  } as SelectedPeriod;
};

export const AppDataContext = React.createContext<AppDataContext>({
  reloadDashboardData: () => {
    return;
  },
  setSelectedPeriod: () => {
    return;
  },
  selectedPeriod: getDefaultPeriod(),
  consumePendingUser: () => {
    return '';
  },
  userApplicationsListIds: [],
  setUserApplicationsListIds: () => {
    return;
  },
  venueApplicationsListIds: [],
  setVenueApplicationsListIds: () => {
    return;
  },
  offerApplicationsListIds: [],
  setOfferApplicationsListIds: () => {
    return;
  },
});

const AppDataContextProvider: React.FC = ({ children }) => {
  const { isAdmin } = useAdminRole();
  const [selectedPeriod, setSelectedPeriod] = React.useState(getDefaultPeriod());
  const [nextPendingUsersId, setNextPendingUsersId] = React.useState<string[]>([]);
  const [userApplicationsListIds, setUserApplicationsListIds] = React.useState<string[]>([]);
  const [venueApplicationsListIds, setVenueApplicationsListIds] = React.useState<string[]>([]);
  const [offerApplicationsListIds, setOfferApplicationsListIds] = React.useState<string[]>([]);

  const { start: loadDashboardData, result: dashboardData } = useAsync(adminGetDashboardDataApi);
  const { start: loadMarkets, result: markets } = useAsync(adminListMarketsAsOptionsApi);
  const { start: loadVenuesMarkets, result: venuesMarkets } = useAsync(
    adminListVenuesMarketsAsOptionsApi,
  );
  const { start: loadApplicationUsersMarkets, result: applicationUsersMarkets } = useAsync(
    adminListApplicationUsersMarketsAsOptionsApi,
  );

  const { start: loadPendingUsers, result: pendingUsers } = useAsync(adminGetNextPendingUserApi);

  // we should reload the data when we change the period

  React.useEffect(() => {
    if (isAdmin) {
      // loadDashboardData(selectedPeriod);
      loadMarkets();
      loadVenuesMarkets();
      loadPendingUsers();
    }
  }, [isAdmin]);

  const reloadDashboardData = React.useCallback(
    () => isAdmin && loadDashboardData(selectedPeriod),
    [isAdmin],
  );

  const setSelectedPeriodCallback = React.useCallback((newSelectedPeriod) => {
    if (new Date(newSelectedPeriod.fromDate) > new Date(newSelectedPeriod.toDate)) {
      return;
    }
    const by =
      dayjs(newSelectedPeriod.toDate).diff(dayjs(newSelectedPeriod.fromDate), 'day') > 60
        ? 'week'
        : 'day';

    const selectedPeriodFormatted = { ...newSelectedPeriod, by };
    setSelectedPeriod(selectedPeriodFormatted);
    loadDashboardData(selectedPeriodFormatted);
  }, []);

  React.useEffect(() => {
    const userIds =
      pendingUsers && pendingUsers.reduce<string[]>((acc, curr) => [...acc, curr.id], []);
    setNextPendingUsersId(userIds || []);
  }, [pendingUsers]);

  const consumePendingUser = React.useCallback(
    (userId: string): string => {
      const filtered = nextPendingUsersId.filter((id: string) => id !== userId);
      if (filtered.length <= 3) loadPendingUsers();
      else setNextPendingUsersId(filtered);
      return filtered[0];
    },
    [nextPendingUsersId, loadPendingUsers],
  );

  const handleLoadApplicationUsersMarkets = (filter: AdminListMarketsFilter) => {
    loadApplicationUsersMarkets({ ...filter });
  };

  return (
    <AppDataContext.Provider
      value={{
        dashboardData,
        logResources: [],
        markets,
        venuesMarkets,
        applicationUsersMarkets,
        reloadDashboardData,
        selectedPeriod,
        setSelectedPeriod: setSelectedPeriodCallback,
        consumePendingUser,
        handleLoadApplicationUsersMarkets,
        userApplicationsListIds,
        setUserApplicationsListIds,
        venueApplicationsListIds,
        setVenueApplicationsListIds,
        offerApplicationsListIds,
        setOfferApplicationsListIds,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export default AppDataContextProvider;
